import { lazy, Suspense, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  CrmBranchCount,
  CrmBreakdownRow,
  CrmReport,
  CrmReportQuery,
  CrmWhatsAppBranchRow,
} from '@optiflow/types';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { useLocale } from '@/hooks/useLocale';
import { CommunicationStatusBadge } from '@/modules/crm/components/communicationLabels';
import { ChangeText } from './ChangeText';
import { CrmRecords, type CrmRecordsRequest } from './CrmRecords';
import { failureReasonLabel } from './crmLabels';

const InventoryCountChart = lazy(() => import('./InventoryCountChart'));

const LOCALE_TAG = { en: 'en-GB', ar: 'ar-EG' } as const;

function Stat({
  label,
  children,
  hint,
  action,
}: {
  label: string;
  children: ReactNode;
  hint?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <Card className="min-w-0 p-4">
      <p className="text-muted text-sm">{label}</p>
      <div className="text-ink mt-2 text-2xl font-semibold">{children}</div>
      {hint ? <div className="text-muted mt-1 text-xs">{hint}</div> : null}
      {action}
    </Card>
  );
}

function Section({
  id,
  title,
  note,
  children,
}: {
  id: string;
  title: string;
  note?: string;
  children: ReactNode;
}) {
  return (
    <section aria-labelledby={id} className="space-y-3">
      <div>
        <h2 id={id} className="text-ink font-semibold">
          {title}
        </h2>
        {note ? <p className="text-muted text-xs">{note}</p> : null}
      </div>
      {children}
    </section>
  );
}

/**
 * The CRM tab.
 *
 * Messages prepared, where they stand, why some failed and why others were withheld — then, as of
 * now, whether each branch's WhatsApp can send and which rule governs it. Failed, cancelled and not
 * sent are always separate rows, "Sent to WhatsApp" never reads as delivered, and runtime carries no
 * comparison because no history of it is kept. Every figure opens into the records behind it.
 */
export function CrmContent({ report, query }: { report: CrmReport; query: CrmReportQuery }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [open, setOpen] = useState<CrmRecordsRequest | null>(null);
  const number = new Intl.NumberFormat(LOCALE_TAG[locale]);
  const count = (value: number) => (
    <span dir="ltr" className="tabular-nums">
      {number.format(value)}
    </span>
  );

  const view = (request: CrmRecordsRequest) => (
    <button
      type="button"
      onClick={() => setOpen(request)}
      className="text-brand-700 mt-2 text-sm font-medium hover:underline"
      aria-label={t('reports.sales.records.viewFor', { figure: request.title })}
    >
      {t('reports.sales.records.view')}
    </button>
  );

  const records = open ? (
    <CrmRecords
      key={`${open.kind}-${open.bucketStatus ?? ''}-${open.bucketMode ?? ''}-${open.bucketReason ?? ''}-${open.branchView ?? ''}`}
      query={query}
      request={open}
      onClose={() => setOpen(null)}
    />
  ) : null;

  const table = (head: ReactNode, body: ReactNode) => (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full min-w-[20rem] text-sm [&_td]:px-2 [&_th]:px-2">
        <thead>
          <tr className="text-muted border-line border-b">{head}</tr>
        </thead>
        <tbody>{body}</tbody>
      </table>
    </div>
  );
  const th = (label: string, end = false) => (
    <th className={`py-2 font-medium ${end ? 'text-end' : 'text-start'}`}>{label}</th>
  );
  const modeHead = (
    <>
      {th(t('reports.crm.columns.total'), true)}
      {th(t('reports.crm.columns.automated'), true)}
      {th(t('reports.crm.columns.manual'), true)}
    </>
  );

  const breakdown = (title: string, rows: CrmBreakdownRow[], unnamed: string) => (
    <Card className="min-w-0 p-4">
      <h3 className="text-ink font-semibold">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-muted mt-3 text-sm">{t('reports.crm.empty')}</p>
      ) : (
        table(
          <>
            {th(t('reports.crm.columns.name'))}
            {modeHead}
          </>,
          rows.map((row) => (
            <tr key={row.id ?? 'none'} className="border-line border-b last:border-b-0">
              <td className="py-2 break-words">
                {(locale === 'ar' && row.labelAr) || row.label || unnamed}
              </td>
              <td className="py-2 text-end">{count(row.total)}</td>
              <td className="py-2 text-end">{count(row.automated)}</td>
              <td className="py-2 text-end">{count(row.manual)}</td>
            </tr>
          )),
        )
      )}
    </Card>
  );

  const branchCounts = (title: string, rows: CrmBranchCount[]) => (
    <Card className="min-w-0 p-4">
      <h3 className="text-ink font-semibold">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-muted mt-3 text-sm">{t('reports.crm.empty')}</p>
      ) : (
        table(
          <>
            {th(t('reports.crm.columns.name'))}
            {th(t('reports.crm.columns.count'), true)}
          </>,
          rows.map((row) => (
            <tr key={row.id} className="border-line border-b last:border-b-0">
              <td className="py-2 break-words">{row.label}</td>
              <td className="py-2 text-end">{count(row.count)}</td>
            </tr>
          )),
        )
      )}
    </Card>
  );

  const runtimeLabel = (row: CrmWhatsAppBranchRow) =>
    !row.configured
      ? t('reports.crm.whatsapp.notSetUp')
      : row.runtimeStatus
        ? t(`crm.whatsappStatus.${row.runtimeStatus}`)
        : t('reports.crm.whatsapp.noSession');
  const attention = (row: CrmWhatsAppBranchRow) => {
    const notes: string[] = [];
    if (row.automationUnavailable) notes.push(t('reports.crm.whatsapp.attention.waiting'));
    if (row.requiresReauth) notes.push(t('reports.crm.whatsapp.attention.reauth'));
    if (row.adminDisabled) notes.push(t('reports.crm.whatsapp.attention.adminDisabled'));
    if (row.runtimeStatus && !row.identityMatches) {
      notes.push(t('reports.crm.whatsapp.attention.identityMismatch'));
    }
    return notes.length === 0 ? t('reports.crm.none') : notes.join(' · ');
  };
  const onOff = (value: boolean) =>
    value ? t('reports.crm.whatsapp.on') : t('reports.crm.whatsapp.off');

  const { volume, outcomes, failures, suppressions, whatsapp, automation } = report;
  const anything = volume || outcomes || failures || suppressions || whatsapp || automation;

  return (
    <div className="space-y-6">
      {report.notApplicable.length > 0 ? (
        <p className="text-muted text-sm">
          {t('reports.crm.notApplicable', {
            sections: report.notApplicable
              .map((section) => t(`reports.crm.sections.${section}`))
              .join(locale === 'ar' ? '، ' : ', '),
          })}
        </p>
      ) : null}
      {!anything ? <p className="text-muted text-sm">{t('reports.crm.noSections')}</p> : null}

      {volume ? (
        <Section
          id="crm-volume"
          title={t('reports.crm.volume.title')}
          note={t('reports.crm.volume.note')}
        >
          <div className="grid gap-4 sm:grid-cols-3">
            <Stat
              label={t('reports.crm.volume.prepared')}
              hint={
                volume.prepared.change ? (
                  <ChangeText basisPoints={volume.prepared.change.pctBasisPoints} />
                ) : null
              }
              action={view({ kind: 'PREPARED', title: t('reports.crm.volume.prepared') })}
            >
              {count(volume.prepared.current)}
            </Stat>
            <Stat
              label={t('reports.crm.volume.automated')}
              action={view({
                kind: 'PREPARED',
                bucketMode: 'AUTOMATED_PROVIDER',
                title: t('reports.crm.volume.automated'),
              })}
            >
              {count(volume.byMode.automated)}
            </Stat>
            <Stat
              label={t('reports.crm.volume.manual')}
              action={view({
                kind: 'PREPARED',
                bucketMode: 'MANUAL_HANDOFF',
                title: t('reports.crm.volume.manual'),
              })}
            >
              {count(volume.byMode.manual)}
            </Stat>
          </div>
          <Card className="min-w-0 p-4">
            <h3 className="text-ink font-semibold">{t('reports.crm.volume.trend')}</h3>
            <Suspense fallback={null}>
              <InventoryCountChart
                label={t('reports.crm.volume.trend')}
                data={volume.trend.buckets.map((bucket, index) => ({
                  label: bucket.from,
                  value: (volume.trend.automated[index] ?? 0) + (volume.trend.manual[index] ?? 0),
                }))}
              />
            </Suspense>
            <details className="mt-2">
              <summary className="text-brand-700 cursor-pointer text-sm font-medium">
                {t('reports.crm.volume.showTable')}
              </summary>
              {table(
                <>
                  {th(t('reports.crm.columns.period'))}
                  {modeHead}
                </>,
                volume.trend.buckets.map((bucket, index) => {
                  const automated = volume.trend.automated[index] ?? 0;
                  const manual = volume.trend.manual[index] ?? 0;
                  return (
                    <tr key={bucket.from} className="border-line border-b last:border-b-0">
                      <td className="py-2" dir="ltr">
                        {bucket.from === bucket.to ? bucket.from : `${bucket.from} – ${bucket.to}`}
                      </td>
                      <td className="py-2 text-end">{count(automated + manual)}</td>
                      <td className="py-2 text-end">{count(automated)}</td>
                      <td className="py-2 text-end">{count(manual)}</td>
                    </tr>
                  );
                }),
              )}
            </details>
          </Card>
          <div className="grid gap-4 lg:grid-cols-3">
            <Card className="min-w-0 p-4">
              <h3 className="text-ink font-semibold">{t('reports.crm.volume.byPurpose')}</h3>
              {volume.byPurpose.length === 0 ? (
                <p className="text-muted mt-3 text-sm">{t('reports.crm.empty')}</p>
              ) : (
                table(
                  <>
                    {th(t('reports.crm.columns.name'))}
                    {modeHead}
                  </>,
                  volume.byPurpose.map((row) => (
                    <tr key={row.purpose} className="border-line border-b last:border-b-0">
                      <td className="py-2">{t(`crm.purpose.${row.purpose}`)}</td>
                      <td className="py-2 text-end">{count(row.total)}</td>
                      <td className="py-2 text-end">{count(row.automated)}</td>
                      <td className="py-2 text-end">{count(row.manual)}</td>
                    </tr>
                  )),
                )
              )}
            </Card>
            {breakdown(t('reports.crm.volume.byBranch'), volume.byBranch, t('reports.crm.none'))}
            {breakdown(
              t('reports.crm.volume.byTemplate'),
              volume.byTemplate,
              t('reports.crm.volume.customText'),
            )}
          </div>
        </Section>
      ) : null}

      {outcomes ? (
        <Section
          id="crm-outcomes"
          title={t('reports.crm.outcomes.title')}
          note={t('reports.crm.outcomes.note')}
        >
          <Card className="min-w-0 p-4">
            <p className="text-muted text-sm">{t('reports.crm.outcomes.acceptedNote')}</p>
            {table(
              <>
                {th(t('reports.crm.columns.status'))}
                {modeHead}
                <th className="py-2" />
              </>,
              <>
                {outcomes.byStatus.map((row) => {
                  const label = t(`crm.status.${row.status}`);
                  return (
                    <tr key={row.status} className="border-line border-b last:border-b-0">
                      <td className="py-2">
                        <CommunicationStatusBadge status={row.status} />
                      </td>
                      <td className="py-2 text-end">{count(row.total)}</td>
                      <td className="py-2 text-end">{count(row.automated)}</td>
                      <td className="py-2 text-end">{count(row.manual)}</td>
                      <td className="py-2 text-end">
                        {row.total > 0
                          ? view({
                              kind: 'OUTCOMES',
                              bucketStatus: row.status,
                              title: `${t('reports.crm.outcomes.title')} — ${label}`,
                            })
                          : null}
                      </td>
                    </tr>
                  );
                })}
                <tr className="font-medium">
                  <td className="py-2">{t('reports.crm.outcomes.total')}</td>
                  <td className="py-2 text-end">{count(outcomes.total.total)}</td>
                  <td className="py-2 text-end">{count(outcomes.total.automated)}</td>
                  <td className="py-2 text-end">{count(outcomes.total.manual)}</td>
                  <td className="py-2 text-end">
                    {outcomes.total.total > 0
                      ? view({ kind: 'OUTCOMES', title: t('reports.crm.outcomes.title') })
                      : null}
                  </td>
                </tr>
              </>,
            )}
          </Card>
        </Section>
      ) : null}

      {failures || suppressions ? (
        <div className="grid gap-6 xl:grid-cols-2 [&>*]:min-w-0">
          {failures ? (
            <Section
              id="crm-failures"
              title={t('reports.crm.failures.title')}
              note={t('reports.crm.failures.note')}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <Stat
                  label={t('reports.crm.failures.failed')}
                  action={view({ kind: 'FAILED', title: t('reports.crm.failures.title') })}
                >
                  {count(failures.failed)}
                </Stat>
                <Stat label={t('reports.crm.failures.outcomeUnknown')}>
                  {count(failures.outcomeUnknown)}
                </Stat>
              </div>
              <Card className="min-w-0 p-4">
                <h3 className="text-ink font-semibold">{t('reports.crm.failures.byReason')}</h3>
                {failures.byReason.length === 0 ? (
                  <p className="text-muted mt-3 text-sm">{t('reports.crm.failures.none')}</p>
                ) : (
                  table(
                    <>
                      {th(t('reports.crm.columns.reason'))}
                      {th(t('reports.crm.columns.count'), true)}
                      <th className="py-2" />
                    </>,
                    failures.byReason.map((row) => {
                      const label = failureReasonLabel(t, row.code);
                      return (
                        <tr key={row.code} className="border-line border-b last:border-b-0">
                          <td className="py-2">{label}</td>
                          <td className="py-2 text-end">{count(row.count)}</td>
                          <td className="py-2 text-end">
                            {view({
                              kind: 'FAILED',
                              bucketReason: row.code,
                              title: `${t('reports.crm.failures.title')} — ${label}`,
                            })}
                          </td>
                        </tr>
                      );
                    }),
                  )
                )}
              </Card>
              {branchCounts(t('reports.crm.failures.byBranch'), failures.byBranch)}
            </Section>
          ) : null}
          {suppressions ? (
            <Section
              id="crm-suppressions"
              title={t('reports.crm.suppressions.title')}
              note={t('reports.crm.suppressions.note')}
            >
              <Stat
                label={t('reports.crm.suppressions.total')}
                action={view({ kind: 'SUPPRESSED', title: t('reports.crm.suppressions.title') })}
              >
                {count(suppressions.total)}
              </Stat>
              <Card className="min-w-0 p-4">
                <h3 className="text-ink font-semibold">{t('reports.crm.suppressions.byReason')}</h3>
                {suppressions.byReason.length === 0 ? (
                  <p className="text-muted mt-3 text-sm">{t('reports.crm.suppressions.none')}</p>
                ) : (
                  table(
                    <>
                      {th(t('reports.crm.columns.reason'))}
                      {th(t('reports.crm.columns.count'), true)}
                      <th className="py-2" />
                    </>,
                    suppressions.byReason.map((row) => {
                      const label = t(`reports.crm.suppressionReason.${row.reason}`);
                      return (
                        <tr key={row.reason} className="border-line border-b last:border-b-0">
                          <td className="py-2">{label}</td>
                          <td className="py-2 text-end">{count(row.count)}</td>
                          <td className="py-2 text-end">
                            {view({
                              kind: 'SUPPRESSED',
                              bucketReason: row.reason,
                              title: `${t('reports.crm.suppressions.title')} — ${label}`,
                            })}
                          </td>
                        </tr>
                      );
                    }),
                  )
                )}
              </Card>
              {branchCounts(t('reports.crm.suppressions.byBranch'), suppressions.byBranch)}
            </Section>
          ) : null}
        </div>
      ) : null}

      {whatsapp ? (
        <Section
          id="crm-whatsapp"
          title={t('reports.crm.whatsapp.title')}
          note={t('reports.crm.whatsapp.note')}
        >
          <div className="grid gap-4 sm:grid-cols-3">
            <Stat
              label={t('reports.crm.whatsapp.automationOn')}
              action={view({
                kind: 'WHATSAPP_BRANCHES',
                branchView: 'AUTOMATION_ENABLED',
                title: t('reports.crm.whatsapp.automationOn'),
              })}
            >
              {count(whatsapp.automationEnabledBranches)}
            </Stat>
            <Stat
              label={t('reports.crm.whatsapp.waiting')}
              hint={t('reports.crm.whatsapp.waitingHint')}
              action={view({
                kind: 'WHATSAPP_BRANCHES',
                branchView: 'UNAVAILABLE',
                title: t('reports.crm.whatsapp.waiting'),
              })}
            >
              {count(whatsapp.automationUnavailableBranches)}
            </Stat>
            <Stat label={t('reports.crm.whatsapp.canSend')}>{count(whatsapp.canSendBranches)}</Stat>
          </div>
          <Card className="min-w-0 p-4">
            {table(
              <>
                {th(t('reports.crm.whatsapp.columns.branch'))}
                {th(t('reports.crm.whatsapp.columns.messaging'))}
                {th(t('reports.crm.whatsapp.columns.automation'))}
                {th(t('reports.crm.whatsapp.columns.identity'))}
                {th(t('reports.crm.whatsapp.columns.runtime'))}
                {th(t('reports.crm.whatsapp.columns.canSend'))}
                {th(t('reports.crm.whatsapp.columns.attention'))}
              </>,
              whatsapp.rows.map((row) => (
                <tr key={row.branchId} className="border-line border-b last:border-b-0">
                  <td className="py-2 break-words">{row.branchName}</td>
                  <td className="py-2">
                    {row.configured ? onOff(row.enabled) : t('reports.crm.whatsapp.notSetUp')}
                  </td>
                  <td className="py-2">{onOff(row.automationEnabled)}</td>
                  <td className="py-2">
                    {row.activeIdentity
                      ? t(`crm.identity.${row.activeIdentity}`)
                      : t('reports.crm.none')}
                  </td>
                  <td className="py-2">{runtimeLabel(row)}</td>
                  <td className="py-2">
                    {row.canSend ? t('reports.crm.yes') : t('reports.crm.no')}
                  </td>
                  <td className="py-2">{attention(row)}</td>
                </tr>
              )),
            )}
          </Card>
        </Section>
      ) : null}

      {automation ? (
        <Section
          id="crm-automation"
          title={t('reports.crm.automation.title')}
          note={t('reports.crm.automation.note')}
        >
          <div className="grid gap-4 sm:grid-cols-3">
            <Stat
              label={t('reports.crm.automation.enabled')}
              action={view({
                kind: 'AUTOMATION_BRANCHES',
                title: t('reports.crm.automation.title'),
              })}
            >
              {count(automation.enabledBranches)}
            </Stat>
            <Stat label={t('reports.crm.automation.disabled')}>
              {count(automation.disabledBranches + automation.withoutRuleBranches)}
            </Stat>
            <Stat label={t('reports.crm.automation.withdrawn')}>
              {count(automation.templateWithdrawnBranches)}
            </Stat>
          </div>
          <Card className="min-w-0 p-4">
            {table(
              <>
                {th(t('reports.crm.automation.columns.branch'))}
                {th(t('reports.crm.automation.columns.source'))}
                {th(t('reports.crm.automation.columns.state'))}
                {th(t('reports.crm.automation.columns.template'))}
              </>,
              automation.rows.map((row) => (
                <tr key={row.branchId} className="border-line border-b last:border-b-0">
                  <td className="py-2 break-words">{row.branchName}</td>
                  <td className="py-2">{t(`reports.crm.automation.source.${row.source}`)}</td>
                  <td className="py-2">
                    {row.enabled ? t('reports.crm.automation.on') : t('reports.crm.automation.off')}
                  </td>
                  <td className="py-2 break-words">
                    {(locale === 'ar' && row.templateNameAr) ||
                      row.templateName ||
                      t('reports.crm.none')}
                    {row.templateActive === false ? (
                      <span className="ms-2">
                        <Badge tone="warning">{t('reports.crm.automation.withdrawnBadge')}</Badge>
                      </span>
                    ) : null}
                  </td>
                </tr>
              )),
            )}
          </Card>
        </Section>
      ) : null}

      {records}
    </div>
  );
}
